import Component from '@glimmer/component';
import { action, set } from '@ember/object';
import { service } from '@ember/service';
import GroupType from '@linc-technologies/linced-common/enums/group-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';

// GroupEditDialog is the extraction of the legacy `{{partial "group-edit-dialog"}}`.
// Callers (admin/groups controller, group-card, filtered-groups-list) all mix in
// `GroupEditMixin`, which owns the form state and the `actions:` hash for the
// group being edited.
//
// The `actions:` hash must stay intact — `admin/groups.hbs`,
// `admin/autogroups.hbs`, and `admin/people/schema/index.hbs` all invoke these
// action names via classic `{{action "toggleGroupDialog"}}` / `{{action
// "deleteGroup" ...}}`, which only resolves against `actions:`. Promoting them
// to top-level methods on the mixin would break those templates.
//
// While those legacy callers exist, this constructor is the least-bad bridge:
// GROUP_ACTIONS lists the mixin actions the dialog template drives; each is
// re-exposed as a plain method that forwards to the same `@group` method, so the template
// can call `{{this.saveGroup}}` / `{{fn this.confirmDelete false}}` instead of
// the deprecated `{{action "name" target=...}}`. Revisit once `group-card` and
// the classic controllers convert to Glimmer.
const GROUP_ACTIONS = [
  'toggleGroupDialog',
  'saveGroup',
  'startUpload',
  'handleUpload',
  'handleUploadError',
  'addTeacher',
  'removeTeacher',
  'addStudent',
  'removeStudent',
  'deleteGroup',
  'confirmDelete',
  'decrementPage',
  'incrementPage',
  'toggleHomeroom',
  'toggleHomeroomLearner',
  'isHomeroomSelected',
];

export default class GroupEditDialog extends Component {
  @service session;

  GroupType = GroupType;
  Jurisdiction = Jurisdiction;

  constructor(owner, args) {
    super(owner, args);
    GROUP_ACTIONS.forEach((name) => {
      this[name] = (...a) => this.args.group[name](...a);
    });
  }

  // The setters below replace the `{{fn (mut @group.x)}}` this template used to
  // carry. They still go through `set()` rather than plain assignment: every
  // property lives on `GroupEditMixin`, a `Mixin.create()` body whose
  // `computed()` dependent keys (`groupLabelValidation` on `groupType`,
  // `studentsPages`/`paginatedStudents` on `limit` and `studentsPage`,
  // `disableGroupings` on `groupGrouping`) only invalidate when the write goes
  // through `set()`. Mixin bodies can't hold `@tracked` fields, so this is as
  // far as the conversion goes until `GroupEditMixin` becomes a native class -
  // the same line the rest of this branch draws (see ac40397fc8, "Held back
  // throughout: Mixin.create() bodies").
  @action
  setGroupDialogTab(tab) {
    set(this.args.group, 'groupDialogTab', tab);
  }

  @action
  clearProfileMedia() {
    set(this.args.group, 'profileMedia', '');
  }

  @action
  setGroupName(name) {
    set(this.args.group, 'groupName', name);
  }

  @action
  setGroupLabel(label) {
    set(this.args.group, 'groupLabel', label);
  }

  // setGroupType swaps in the whole `GroupType.values` entry. The old
  // `(mut @group.groupType.value)` wrote `value` onto the entry currently
  // selected, and those entries are shared app-wide, so changing a group's
  // visibility corrupted the enum for the rest of the session. Replacing the
  // entry also lets `groupTypeLabel`, whose dependent key is `groupType`, pick
  // the change up.
  @action
  setGroupType(value) {
    set(this.args.group, 'groupType', GroupType.getObject(value));
  }

  @action
  setGroupTeam(team) {
    set(this.args.group, 'groupTeam', team);
  }

  @action
  setGroupGrouping(grouping) {
    set(this.args.group, 'groupGrouping', grouping);
  }

  @action
  setGroupStatus(status) {
    set(this.args.group, 'groupStatus', status);
  }

  @action
  setStudentsLimit(limit) {
    set(this.args.group, 'limit', limit);
  }

  @action
  setStudentsPage(page) {
    set(this.args.group, 'studentsPage', page);
  }

  @action
  setHomeroom(homeroom) {
    set(this.args.group, 'homeroom', homeroom);
  }
}
